import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { Col, Image, Row } from "react-bootstrap";
import { object, string } from 'yup';
import { useForm } from 'react-hook-form';
import { Button, CircularProgress } from '@mui/material';
import { Chart } from 'chart.js';
import NumberFormatDollars from '../../../../Helpers/Formatters/NumberFormatDollars';
import AmericoCoverageLengths from "../../../../Helpers/Lists/CoverageLengths/AmericoCoverageLengths";
import { PaymentAmount, RemainingBalance, RatePerPeriodDecimal } from '../../../../Helpers/LoanCalc';
import { TextInput, SelectInput } from "../../../../Helpers/Inputs";
import CurrencyFormat from '../../../../Helpers/Formatters/CurrencyFormat';
import QuoteFor from "../../../Common/QuoteFor/QuoteFor";
import AmericoQuoteCard from '../../../Common/Cards/AmericoQuoteCard'
import '../../LoanProtection.scss';

export default function QuoteResultsSection(props) {
  const loadingClass = props.loading ? 'loading' : '';

  const [loanInterestRate, setLoanInterestRate] = useState('3.50');
  const [loanLength, setLoanLength] = useState(props.fields.term);
  const [loanAmount, setLoanAmount] = useState(props.fields.coverageAmount);
  const [isCompanyDetailsDialogOpen, setIsCompanyDetailsDialogOpen] = useState(false);
  const [compareToTraditionalIsExpanded, setCompareToTraditionalIsExpanded] = useState(false);
  const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const { register, formState: { errors }, triggerValidation } = useForm({
    validationSchema: QuoteResultsSection.ValidationSchema,
    mode: 'all',
    reValidateMode: 'all',
  });

  useEffect(() => {
    if (props.fields.coverageAmount === null || props.fields.term === null) {
      return;
    }
    setLoanLength(props.fields.term);

    setLoanAmount(props.fields.coverageAmount);

  }, [props.fields.coverageAmount, props.fields.term]);

  const handleLoanInterestRateChange = (interestRate) => {
    if (interestRate > 0 && interestRate < 100) {
      setLoanInterestRate(interestRate);
    }
    updateChart();
  };

  const handleLoanAmountChange = (loanAmount) => {
    setLoanAmount(loanAmount)
    triggerValidation('loanAmount');
  };

  const handleTermChange = (term) => {
    setLoanLength(term);
    updateChart();
  };

  useEffect(() => {
    if (!props.loading) updateChart();
  });

  function updateChart() {
    const ctx = document.getElementById('chartCanvas');
    if (!ctx) {
      return;
    }

    if (window.loanChart) {
      window.loanChart.destroy();
    }

    const loanLengthInt = parseInt(loanLength, 10);
    const rate = RatePerPeriodDecimal(loanInterestRate, 12);
    const payment = PaymentAmount(loanAmount, rate, 12 * loanLengthInt);

    var coverageNeeds = [];
    for (var x = 0; x < (loanLengthInt * 12); x += 12) {
      coverageNeeds.push(Math.round(RemainingBalance(loanAmount, rate, payment, x)));
    }

    var deathBenefits = [];
    var deathBenefitsLabel = [];
    var j = 0;
    if (props.deathBenefitSchedule) {
      while (j < props.deathBenefitSchedule.length - 1) {
        if (props.deathBenefitSchedule[j].deathBenefit) {
          deathBenefits.push(props.deathBenefitSchedule[j].deathBenefit.toFixed(2));
          deathBenefitsLabel.push(formatter.format(props.deathBenefitSchedule[j].deathBenefit));
        }
        j += 12;
      }
    }
    var traditionalData = [];
    for (var f = 0; f < props.fields.term; f++) {
      if (props.fields.coverageAmount) {
        traditionalData.push(parseInt(props.fields.coverageAmount, 10).toFixed(2));
      }
    }

    var chartDatasets = [
      {
        data: coverageNeeds,
        borderColor: '#1EA6B8',
        backgroundColor: '#1EA6B8',
        borderWidth: '4',
        type: 'line',
        fill: false,
        pointStyle: 'line',
        label: `Estimated loan balance over ${loanLength} years`,
        order: 1,
      },
      {
        data: deathBenefits,
        backgroundColor: '#C1EABC',
        label: `Your coverage over ${props.fields.term} years`,
        order: 2,
      },
    ]

    if (compareToTraditionalIsExpanded) {
      chartDatasets.push({
        data: traditionalData,
        borderColor: 'red',
        borderWidth: '4',
        backgroundColor: 'red',
        type: 'line',
        fill: false,
        pointStyle: 'line',
        label: 'Cost of traditional term',
        order: 3,

      })
    }

    window.loanChart = new Chart(ctx, {
      type: 'bar',
      data: {
        datasets: chartDatasets,
        labels: deathBenefitsLabel,
      },
      options: {
        maintainAspectRatio: false,
        responsive: true,
        legend: {
          position: 'bottom',
          labels: { usePointStyle: true },
          fullWidth: true,
        },
        tooltips: {
          enabled: true,
          callbacks: {
            label: function (tooltipItem, data) {
              var label = data.datasets[tooltipItem.datasetIndex].label || '';
              if (label) {
                label += ': ';
              }
              label += formatter.format(tooltipItem.yLabel);
              return label;
            }
          }
        },
        animation: {
          duration: 0,
        },
        scales: {
          gridLines: { drawBorder: true },
          xAxes: [{
            ticks: {
              display: false,
            },
            gridLines: {
              display: false,
            },
          }],
          yAxes: [{
            ticks: {
              callback: (v) => {
                return `$${v / 1000}k`;
              },
              stepSize: props.fields.coverageAmount > 100000 ? 100000 : 10000,
              display: false,
            },
            gridLines: {
              display: true,
              lineWidth: 1,
              drawBorder: false,
            }
          }],
        },
      },
    });
  }

  if (props.loading) {
    return (
      <div className="loading-container mb-5">
        <CircularProgress />
      </div>
    )
  }

  return (
    <div>
      <div className="quote-result-section">
        <QuoteFor coverageAmount={props.fields.coverageAmount} separator productType={`${props.fields.term} Year Decreasing Term`} />
      </div>
      <div className="quoter">
        <div className="quote-card form-data mb-0">
          <form className="quote-form form-data">
            <Row>
              <Col>
                <div>
                  <h4 className="fw-bold loan-balance-text">{props.fields.healthClass === 'best' ? 'LoanMatch Protection Option' : 'Only Pay for What You Need'}</h4>
                  <p className="quote-details-label loan-balance-tool">Use our loan balance tool to compare the LoanMatch Protector Decreasing Term life insurance coverage amount with an estimated loan balance over time.</p>
                </div>
              </Col>
            </Row>
            <Row className='loan-balance-tool'>
              <Col sm={4}>
                <div className="mb-3">
                  <SelectInput
                    name="interestRate"
                    label="Loan Interest Rate"
                    id="interest-rate"
                    value={loanInterestRate}
                    onChange={e => handleLoanInterestRateChange(e.target.value)}
                    selectOptions={[
                      { label: "1.50%", value: "1.50" },
                      { label: "2.00%", value: "2.00" },
                      { label: "2.50%", value: "2.50" },
                      { label: "3.00%", value: "3.00" },
                      { label: "3.50%", value: "3.50" },
                      { label: "4.00%", value: "4.00" },
                      { label: "4.50%", value: "4.50" },
                      { label: "5.00%", value: "5.00" },
                      { label: "5.50%", value: "5.50" },
                      { label: "6.00%", value: "6.00" },
                    ]}
                    disabled={props.loading}
                    hideEmptyOption
                  />
                </div>
              </Col>
              <Col sm={4}>
                <div className="mb-3">
                  <SelectInput
                    name="loanTerm"
                    label="Loan Term"
                    id="loan-term"
                    value={loanLength}
                    onChange={e => handleTermChange(e.target.value)}
                    selectOptions={AmericoCoverageLengths()}
                    disabled={props.loading}
                    hideEmptyOption
                  />
                </div>
              </Col>
              <Col sm={4}>
                <div className="mb-3">
                  <TextInput
                    name="loanAmount"
                    label="Loan Amount"
                    id="loan-amount"
                    value={loanAmount}
                    onChange={e => handleLoanAmountChange(e.target.value)}
                    onBlur={updateChart}
                    InputProps={{
                      inputComponent: NumberFormatDollars,
                    }}
                    disabled={props.loading}
                    errors={errors.loanAmount}
                    inputRef={register}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col className={loadingClass}>
                <canvas id="chartCanvas" width="300" height="100" className="loanChart"></canvas>
              </Col>
            </Row>
            <Row className='quote-underwriting-verbiage'>
              <div className='col-md-3 americoImage'>
                <Image className="image-property" src={`${process.env.PUBLIC_URL}/americo/Americo.png`} alt="Americo" />
              </div>
              <div className='col-md-4 mt-3'>
                <div>
                  <span>
                    <CurrencyFormat className="quote-amount-dollars" removeSymbol number={props.loading ? 0 : props.monthlyRateAmerico} />
                  </span>
                  <span className="quote-amount-mo"> /mo</span>
                </div>
              </div>
              <Col sm={5} className='phone-diplay mt-3'>
                <div className="quote-underwriting-time text-end">
                  Apply and buy in <span className="primary-color">10-15 minutes</span>
                </div>
              </Col>
            </Row>
            <Row className={loadingClass}>
              <Col sm={7} className='quoter-company-detail'>
                <p className="quote-details-label mb-0">
                  This unique product’s coverage amount decreases monthly and is designed to match a decreasing financial need.
                </p>
                <Button onClick={() => setIsCompanyDetailsDialogOpen(true)} variant="text" color="primary" className="company-details" >
                  COMPANY & PRODUCT DETAILS
                </Button>

                <AmericoQuoteCard
                  onNext={props.onNext}
                  logoUrl={props.logoUrl}
                  monthlyRate={props.monthlyRateAmerico}
                  show={isCompanyDetailsDialogOpen}
                  onHide={() => setIsCompanyDetailsDialogOpen(false)} />
              </Col>
              <Col sm={5} className='quoter-button phone-diplay'>
                <Button type="submit" variant="contained" color="primary" disabled={props.loading && !props.canContinue} className="d-flex ms-auto continueButton" onClick={(e) => props.onNext(e, props.monthlyRate, props.ageDetermination)}>Continue</Button>
              </Col>
            </Row>
          </form>
        </div>

      </div>
    </div>
  )
};

QuoteResultsSection.ValidationSchema = object().shape({
  loanAmount: string().test('MinFunc', 'Minimum value of $10,000', (value) => {
    return value && parseFloat(value.split('$')[1].replace(/,/g, '')) >= 10000;
  })
});

QuoteResultsSection.propTypes = {
  fields: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  monthlyRateAmerico: PropTypes.number,
  deathBenefitSchedule: PropTypes.array,
  loading: PropTypes.bool,
};
